
import React, { useState } from 'react';
import { LogoutIcon, CienCuadrasLogoImg, XMarkIcon, Squares2X2Icon, IdentificationIcon, UserCircleIcon } from './Icons'; 
import { AppUser, UserRole } from '../types';
import { IdentityFormModal } from './broker/identity/IdentityFormModal';
import { VirtualCarnetModal } from './broker/identity/VirtualCarnetModal';
import { EditProfileModal } from './broker/identity/EditProfileModal';
import { AdvisorModal } from './broker/identity/AdvisorModal';

const getRoleBadgeStyle = (role: UserRole) => {
    switch (role) {
      case UserRole.SUPERADMIN:
        return 'bg-red-100 text-red-800 border border-red-200';
      case UserRole.ADMIN:
        return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
      case UserRole.BROKER:
        return 'bg-sky-100 text-sky-800 border border-sky-200';
      case UserRole.BROKER_INTERNO:
        return 'bg-sky-100 text-sky-800 border border-sky-200';
      case UserRole.BROKER_PREMIUM:
        return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
      case UserRole.CLIENTE:
        return 'bg-purple-100 text-purple-800 border border-purple-200';
      case UserRole.COMERCIAL_INTERNO:
        return 'bg-teal-100 text-teal-800 border border-teal-200';
      case UserRole.BROKERS_LEADS:
        return 'bg-sky-100 text-sky-800 border border-sky-200';
      case UserRole.SIN_ROL:
      default:
        return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
};

interface NewDashboardHeaderProps {
  onLogout?: () => void;
  appUser?: AppUser | null;
  isNewlyAddedMode?: boolean;
}

export const NewDashboardHeader: React.FC<NewDashboardHeaderProps> = ({ onLogout, appUser, isNewlyAddedMode }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isIdentityFormOpen, setIsIdentityFormOpen] = useState(false);
  const [isCarnetModalOpen, setIsCarnetModalOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isAdvisorModalOpen, setIsAdvisorModalOpen] = useState(false);

  const isClient = appUser?.role === UserRole.CLIENTE;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.history.replaceState(null, '', '/');
    window.location.hash = '';
    window.dispatchEvent(new Event('popstate'));
  };

  const handleExitNewlyAdded = () => {
    window.location.hash = '';
  };

  const handleOpenCarnet = () => {
    if (!appUser) return;
    if (appUser.carnetConfigurado) {
        setIsCarnetModalOpen(true);
    } else {
        setIsIdentityFormOpen(true);
    }
  };

  return (
    <>
      <header id="main-app-header" className="bg-white shadow-md sticky top-0 z-[100]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-shrink-0">
              <a href="/" onClick={handleLogoClick} className="cursor-pointer" aria-label="Volver al inicio">
                <CienCuadrasLogoImg />
              </a>
            </div>

            {/* Opciones de perfil, carnet y cerrar sesión en resolución Desktop */}
            <div className="hidden md:flex items-center ml-auto space-x-4">
              {appUser && appUser.role && (
                <div className="flex items-center gap-3">
                  {(appUser.role === UserRole.BROKER || appUser.role === UserRole.BROKER_PREMIUM) && (
                    <button
                        onClick={() => setIsProfileOpen(true)}
                        className="px-3 py-1.5 border border-slate-300 text-slate-700 hover:text-slate-950 text-xs font-black uppercase rounded-xl hover:bg-slate-50 flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                    >
                        <UserCircleIcon className="w-4 h-4 text-slate-500" />
                        <span>Mi Perfil</span>
                    </button>
                  )}
                  {!isClient && (
                    <button
                        onClick={() => setIsAdvisorModalOpen(true)}
                        className="px-3 py-1.5 border-2 border-sky-600 text-sky-600 text-xs font-black uppercase rounded-xl hover:bg-sky-50 flex items-center gap-2 transition-all active:scale-95 shadow-sm"
                    >
                        <UserCircleIcon className="w-4 h-4" />
                        <span>Conoce tu asesor</span>
                    </button>
                  )}
                  <div className={`px-3 py-1.5 text-sm font-semibold rounded-full whitespace-nowrap capitalize ${getRoleBadgeStyle(appUser.role)}`}>
                      {appUser.role.replace(/_/g, ' ').toLowerCase()}
                  </div>
                </div>
              )}
              {onLogout && !isNewlyAddedMode && !isClient && (
                  <button
                      onClick={onLogout}
                      className="px-3 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center space-x-2"
                      aria-label="Cerrar sesión"
                  >
                      <LogoutIcon className="w-4 h-4" />
                      <span>Salir</span>
                  </button>
              )}
            </div>
            
            {/* Controles para Móviles/Tablets */}
            <div className="md:hidden flex items-center">
              {appUser && !isClient && (
                  <button
                      onClick={() => setIsAdvisorModalOpen(true)}
                      className="p-2 mr-1 text-sky-600 hover:text-sky-800"
                      aria-label="Conoce tu asesor"
                      title="Conoce tu asesor"
                  >
                      <UserCircleIcon className="w-6 h-6" />
                  </button>
              )}
              {onLogout && !isNewlyAddedMode && !isClient && (
                    <button
                        onClick={onLogout}
                        className="p-2 mr-1 text-red-600 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-500 rounded-md"
                        aria-label="Cerrar sesión (móvil)"
                      >
                        <LogoutIcon className="w-6 h-6" />
                      </button>
              )}
              <button 
                onClick={toggleMobileMenu}
                className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500 p-2 rounded-md"
                aria-label="Abrir menú principal"
                aria-expanded={isMobileMenuOpen}
              >
                <svg className="h-6 w-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Menú Móvil */}
        {isMobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-30 bg-white overflow-y-auto" id="mobile-menu">
            <div className="pt-5 pb-6 px-4 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <CienCuadrasLogoImg />
                <button
                  onClick={toggleMobileMenu}
                  className="-mr-2 p-2 rounded-md inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500"
                  aria-label="Cerrar menú"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                {appUser && appUser.role && (
                  <div className="px-2 py-3 flex items-center justify-between border-b border-gray-100">
                    <span className="text-sm font-medium text-gray-500">Perfil:</span>
                    <div className={`px-3 py-1.5 text-sm font-semibold rounded-full whitespace-nowrap capitalize ${getRoleBadgeStyle(appUser.role)}`}>
                        {appUser.role.replace(/_/g, ' ').toLowerCase()}
                    </div>
                  </div>
                )}
                
                {appUser && (appUser.role === UserRole.BROKER || appUser.role === UserRole.BROKER_PREMIUM) && (
                   <button
                    onClick={() => { toggleMobileMenu(); setIsProfileOpen(true); }}
                    className="w-full px-4 py-3 text-base font-bold text-slate-700 bg-slate-50 rounded-xl flex items-center justify-center gap-2 transition-colors active:bg-slate-100"
                   >
                     <UserCircleIcon className="w-6 h-6 text-slate-500" />
                     <span>Editar Perfil</span>
                   </button>
                )}
                
                {appUser && !isClient && (
                   <button
                    onClick={() => { toggleMobileMenu(); setIsAdvisorModalOpen(true); }}
                    className="w-full px-4 py-3 text-base font-bold text-sky-700 bg-sky-50 rounded-xl flex items-center justify-center gap-2 transition-colors active:bg-sky-100"
                   >
                     <UserCircleIcon className="w-6 h-6" />
                     <span>Conoce tu asesor</span>
                   </button>
                )}
                {onLogout && !isNewlyAddedMode && !isClient && (
                      <button
                          onClick={() => { onLogout(); toggleMobileMenu(); }}
                          className="w-full px-4 py-3 text-base font-medium text-red-700 bg-red-50 rounded-xl hover:bg-red-100 flex items-center justify-center gap-2 transition-colors"
                      >
                          <LogoutIcon className="w-5 h-5" />
                          <span>Salir</span>
                      </button>
                )}
              </div>
            </div>
          </div>
        )}
      </header>

      {isNewlyAddedMode && (
        <button
          onClick={handleExitNewlyAdded}
          className="fixed bottom-8 right-8 z-50 px-6 py-3 bg-red-600 text-white font-bold rounded-lg shadow-xl hover:bg-red-700 transition-all duration-300 ease-in-out transform hover:scale-105 flex items-center space-x-3"
          aria-label="Ver todos los inmuebles"
        >
          <Squares2X2Icon className="w-5 h-5 flex-shrink-0"/>
          <span className="whitespace-nowrap">Ver todos los inmuebles</span>
        </button>
      )}

      {isIdentityFormOpen && appUser && (
        <IdentityFormModal 
            isOpen={isIdentityFormOpen} 
            onClose={() => setIsIdentityFormOpen(false)} 
            appUser={appUser} 
            onSuccess={() => { setIsIdentityFormOpen(false); setIsCarnetModalOpen(true); }}
        />
      )}

      {isCarnetModalOpen && appUser && (
        <VirtualCarnetModal 
            isOpen={isCarnetModalOpen} 
            onClose={() => setIsCarnetModalOpen(false)} 
            appUser={appUser} 
        />
      )}

      {isAdvisorModalOpen && appUser && (
        <AdvisorModal
            isOpen={isAdvisorModalOpen}
            onClose={() => setIsAdvisorModalOpen(false)}
            appUser={appUser}
        />
      )}

      {isProfileOpen && appUser && (
        <EditProfileModal 
            isOpen={isProfileOpen} 
            onClose={() => setIsProfileOpen(false)} 
            appUser={appUser} 
            onOpenCarnet={handleOpenCarnet}
        />
      )}
    </>
  );
};
